import { createAction, isRejected } from '@reduxjs/toolkit'

import { ThreadMessageType, ChatThreadUser, LiveChatThread } from 'apps/chat/constants'
import {
  ThreadsSortingTypes,
  ThreadAssignment,
  MsginEvent,
} from 'apps/chat/constants/LiveChatInterfaces'
import { UserThreadNoteSocketEvents } from 'apps/chat/constants/LiveChatNoteTypes'
import * as atypes from 'apps/chat/constants/LiveChatReduxActionTypes'
import { ThreadStatus } from 'apps/chat/interfaces/statuses'
import {
  AssignEvent,
  LiveChatNote,
  ThreadsSocketData,
  ThreadUserReadSocketData,
  UserThreadNewEvent,
} from 'apps/chat/interfaces/threadsSocketSchema'
import {
  threadActions as actions,
  threadSelectors,
} from 'apps/chat/reducers/slices/threadListSlice'
import {
  setMessageReceived,
  setThreadAssignedToMe,
  setThreadUnassigned,
} from 'apps/chat/redux/notificationsSlice'
import {
  getThreadStatusesByStatusFilterAndFolder,
  getIsAdvancedFilterApplied,
  getIsThreadSatisfiesFilter,
  getCurrentThreadItem,
  getCurrentId,
} from 'apps/chat/selectors/threadSelectors'
import { NAMESPACE } from 'apps/followReply/store/constants'
import { IAsyncThunkAction, IThunkAction } from 'common/core/interfaces/actions'
import { getIsAccountFlagEnabled, getUserId } from 'common/core/selectors/appSelectors'
import { createAsyncAction } from 'shared/lib/redux'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'

import { applyChangesToThreads } from './utils'

export const threadUpdatedNotification = createAction<void>('threads/updatedNotification')

const handleLabelsUpdate = (
  item: ThreadsSocketData,
  isRequiredRequest = false,
): IAsyncThunkAction<ThreadsSocketData | undefined> => {
  return async (dispatch, getState) => {
    const state = getState()
    const threadId = item.user_id
    const currentThread = threadSelectors.getItemById(state, threadId)
    const hasUser = item.user || currentThread?.user

    let labels
    if (item.user?.labels) {
      labels = item.user.labels
    } else if (currentThread?.user?.labels) {
      labels = currentThread.user.labels
    }

    const mergedUser = hasUser
      ? {
          ...(currentThread?.user ? { ...currentThread.user } : {}),
          ...(item.user ? { ...item.user } : {}),
          ...(labels ? { labels } : {}),
        }
      : undefined

    let updatedItem = {
      ...item,
      ...(mergedUser ? { user: mergedUser } : {}),
    }

    if (!currentThread && isRequiredRequest) {
      const response = await dispatch(
        actions.fetchThreadPartialDetails({ threadId, withLabels: true }),
      )

      if (isRejected(response)) return

      const user = response.payload?.thread?.user

      updatedItem = {
        ...updatedItem,
        ...(user ? { user } : {}),
      }
    }

    return updatedItem as ThreadsSocketData
  }
}

export const updatedNotification = (item: ThreadsSocketData): IThunkAction => {
  return (dispatch, getState) => {
    const state = getState()
    const threadId = item.user_id
    const currentThread = threadSelectors.getItemById(state, threadId)

    const updatedItem = { ...item, id: threadId }

    if (currentThread) {
      dispatch(actions.updateItem({ id: threadId, data: updatedItem }))
    } else {
      dispatch(actions.appendItem({ item: updatedItem }))
    }

    dispatch(updateThreadPosition(updatedItem))
    dispatch(threadUpdatedNotification())
  }
}

const updateThreadPosition =
  (item: ThreadsSocketData): IThunkAction =>
  (dispatch, getState) => {
    const state = getState()
    const { chat } = state
    const threadState = chat.threads
    const threadId = item.user_id

    const currentThread = threadSelectors.getItemById(state, threadId)
    const topThread = threadSelectors.getList(state)?.[0]
    const isNewestSort = threadState.threadsSorting === ThreadsSortingTypes.NEWEST
    const isNewMessage = item?.timestamp >= topThread?.timestamp

    if (currentThread && isNewestSort && isNewMessage) {
      dispatch(actions.deleteItem({ id: threadId }))
      dispatch(actions.prependItem({ item: { ...item, id: threadId } }))
    }
  }

export const closeAllThreadsNotification = (threadsIds: number[]): IThunkAction => {
  return (dispatch) => {
    dispatch(
      applyChangesToThreads(threadsIds, {
        lc_thread_status: ThreadStatus.CLOSED,
      }),
    )
  }
}

export const openAllThreadsNotification = (threadsIds: number[]): IThunkAction => {
  return (dispatch) => {
    dispatch(
      applyChangesToThreads(threadsIds, {
        lc_thread_status: ThreadStatus.OPENED,
      }),
    )
  }
}

export const assignThreadsNotification = (threadsIds: number[]): IThunkAction => {
  return (dispatch, getState) => {
    const state = getState()
    const { chat } = state

    dispatch(
      applyChangesToThreads(threadsIds, {
        assignment: {
          user_id: chat.threads.assignee,
        } as unknown as ThreadAssignment,
      }),
    )

    dispatch(actions.setAssignee(null))
  }
}

export const unassignThreadsNotification = (threadsIds: number[]): IThunkAction => {
  return (dispatch) => {
    dispatch(
      applyChangesToThreads(threadsIds, {
        assignment: null,
        lc_thread_status: ThreadStatus.NEW,
      }),
    )

    dispatch(actions.setAssignee(null))
  }
}

export const handleThreadUpdatedNotification = (socketData: ThreadsSocketData): IThunkAction => {
  return async (dispatch, getState) => {
    let detailedSocketData = socketData
    const state = getState()
    const isLabelsEnabled = getIsAccountFlagEnabled(state, AccountFlag.ENABLE_LC_LABELS)

    if (isLabelsEnabled) {
      const response = await dispatch(handleLabelsUpdate(socketData))
      if (response) {
        detailedSocketData = response
      }
    }

    dispatch(updatedNotification(detailedSocketData))
    dispatch(actions.updatedCurrentItemNotification(detailedSocketData))
  }
}

export const threadUserReadNotification =
  (data: ThreadUserReadSocketData): IThunkAction =>
  (dispatch) => {
    dispatch({ type: atypes.THREAD_USER_READ, id: data.id, data: data })
  }

export const handleThreadMessagedNotification = createAsyncAction(
  `${NAMESPACE}/handleThreadMessagedNotification`,
  async (socketData: ThreadsSocketData, { dispatch, getState }) => {
    const state = getState()
    const currentUserId = getUserId(state)
    const assignedAgentId = socketData?.assignment?.user_id
    const { user_id: userId } = socketData

    if (
      currentUserId === assignedAgentId &&
      socketData?.last_lc_event?.type?.includes(ThreadMessageType.MSG_IN)
    ) {
      dispatch(setMessageReceived(Object.assign({}, socketData as LiveChatThread<MsginEvent>)))
    }

    const isThreadMatchStatusFilter = getThreadStatusesByStatusFilterAndFolder(state).includes(
      socketData?.lc_thread_status,
    )
    const isFilterApplied = getIsAdvancedFilterApplied(state)
    const isNeededToCheckThreadSatisfiesCurrentFilter = Boolean(
      isThreadMatchStatusFilter && isFilterApplied,
    )

    if (isNeededToCheckThreadSatisfiesCurrentFilter) {
      await dispatch(actions.checkIsThreadSatisfiesCurrentFilter(userId))
      const isThreadSatisfiesFilter = getIsThreadSatisfiesFilter(getState(), userId)

      if (!isThreadSatisfiesFilter) {
        dispatch(actions.deleteItem({ id: socketData.user_id }))
        dispatch(actions.decreaseTotalThreadsCount(undefined))
        return
      }
    }

    let detailedSocketData = socketData
    const isLabelsEnabled = getIsAccountFlagEnabled(state, AccountFlag.ENABLE_LC_LABELS)

    if (isLabelsEnabled) {
      const response = await dispatch(handleLabelsUpdate(socketData, true))
      if (response) {
        detailedSocketData = response
      }
    }

    dispatch(updatedNotification(detailedSocketData))
    dispatch(actions.updatedCurrentItemNotification(detailedSocketData))
  },
)

export const updateCurrentThreadNotes = (socketData: LiveChatNote): IThunkAction => {
  return (dispatch, getState) => {
    const currentThreadItem = getCurrentThreadItem(getState())

    if (
      currentThreadItem?.user_id === socketData?.user_id &&
      UserThreadNoteSocketEvents.includes(socketData?.type)
    ) {
      dispatch(
        actions.updatedCurrentItemNotification({
          ...currentThreadItem,
          notes: [socketData, ...(currentThreadItem?.notes || [])],
        }),
      )
    }
  }
}

export const reloadThreads = createAsyncAction(
  `${NAMESPACE}/reloadThreads`,
  async (_, { dispatch, getState }) => {
    const state = getState()

    dispatch(actions.dropThreadsList())
    dispatch(actions.fetchThreads())

    const currentThreadId = getCurrentId(state)

    if (currentThreadId) {
      dispatch(actions.fetchThread(currentThreadId))
    }
  },
)

export const handleThreadAssignedEvent = (socketData?: AssignEvent): IThunkAction => {
  return (dispatch, getState) => {
    if (!socketData?.model?.to_user_id) return

    const state = getState()
    const currentUserId = getUserId(state)
    const assignedUserId = socketData?.model?.to_user_id

    if (currentUserId === assignedUserId) {
      dispatch(setThreadAssignedToMe(Object.assign({}, socketData)))
    }
  }
}

export const handleUserThreadNewEvent = (socketData?: UserThreadNewEvent): IThunkAction => {
  return (dispatch) => {
    if (socketData?.model?.before === 'closed') {
      dispatch(setThreadUnassigned(Object.assign({}, socketData)))
      dispatch(actions.increaseTotalThreadsCount(undefined))
    }
  }
}

export const handleUpdateSubscriberInfo = (socketData?: ChatThreadUser): IThunkAction => {
  return (dispatch) => {
    if (!socketData || !socketData?.user_id) return

    const threadId = socketData.user_id
    const user = socketData

    dispatch(actions.updateThreadSubscriberInfo({ threadId, user }))
    dispatch(actions.updateItem({ id: threadId, data: { user } }))
  }
}
